import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IChangeStatus,
  IChangeStatusData,
  IChannelsResponse,
  IContent,
  ICreateLetterRequest,
  IMassages,
  IMassagesResponse,
  INote,
  INoteRequest,
  ISupportsInfo,
  ISupportState,
  ITicketsRequest,
  ITicketsResponse,
  ITypes,
  IUploadFileRequred,
  IUsers,
  IUsersRequest,
} from './types';
import { onJsonParseBigin } from 'untils/jsonParse';
import { toUnixSeconds } from 'untils/centrifuge';
import { getForwardMedia } from 'untils/getForwardMedia';

const mergeUniqueBy = <T, K extends keyof T>(arr: T[], by: K) => {
  const seen = new Set<T[K]>();
  const res: T[] = [];
  for (const item of arr) {
    const key = item[by];
    if (!seen.has(key)) {
      seen.add(key);
      res.push(item);
    }
  }
  return res;
};

// Побудова ключа запиту без page/per_page
const buildUsersQueryKey = (req: IUsersRequest | undefined) => {
  if (!req) return '∅';
  const {
    asc,
    country_code,
    country_name,
    type_id,
    status,
    search,
    order_by,
    resolve,
    resolver_id,
    date_from,
    date_to,
    timeFrom,
    timeTo,
    user_id,
  } = req;

  const normDate = (d: string | Date | undefined) =>
    d ? new Date(d).toISOString().slice(0, 10) : null;

  return JSON.stringify({
    asc: !!asc,
    country_code: country_code || null,
    country_name: country_name || null,
    type_id: type_id ?? null,
    status: status ?? null,
    search: (search ?? '').trim().toLowerCase(),
    order_by: order_by || null,
    resolve: resolve ?? null,
    resolver_id: resolver_id ?? null,
    date_from: normDate(date_from),
    date_to: normDate(date_to),
    timeFrom: timeFrom || null,
    timeTo: timeTo || null,
    user_id: user_id ?? null,
  });
};

// Payload для успішного отримання користувачів
export type UsersSuccessPayload = {
  users: IUsers;
  append: boolean;
};

export const initialState: ISupportState & {
  lastUsersQueryKey?: string | null;
  pendingUsersQueryKey?: string | null;
} = {
  users: null,
  types: null,
  massages: null,
  chatList: [],
  subscribeAll: null,
  notes: [
    {
      body: '',
      epoch: '',
      id: null,
      offset: null,
      reply_id: null,
      support_id: null,
      updated_at: '',
      user_id: null,
      ut: '',
      created_at: '',
      name: '',
      text: {
        String: '',
      },
    },
  ],
  client: '',
  reportsLoader: false,
  uploadLoader: false,
  tickets: null,
  ticket: false,
  search: '',
  lastUsersQueryKey: null,
  pendingUsersQueryKey: null,
};

const support = createSlice({
  name: '@@support',
  initialState,
  reducers: {
    sendLetterRequest: (state, _action: PayloadAction<ICreateLetterRequest>) => {
      state.reportsLoader = true;
    },

    getLetterSuccess: (state, _action: PayloadAction<IChannelsResponse>) => {
      state.reportsLoader = false;
    },

    getUsersRequest: (state, action: PayloadAction<IUsersRequest>) => {
      state.reportsLoader = true;
      const key = buildUsersQueryKey(action.payload);
      state.pendingUsersQueryKey = key;
      if ((action.payload.current_page ?? 1) === 1) {
        state.lastUsersQueryKey = key;
      }
    },

    getUsersSuccess: (state, action: PayloadAction<UsersSuccessPayload>) => {
      const { users, append } = action.payload;
      state.reportsLoader = false;

      const key = state.pendingUsersQueryKey ?? state.lastUsersQueryKey;
      const sameQuery = key && state.lastUsersQueryKey === key;
      const canAppend = append && !!sameQuery;

      if (!canAppend) {
        state.users = users;
      } else {
        const prev = state.users?.data ?? [];
        const next = users?.data ?? [];
        state.users = {
          total: users?.total ?? state.users?.total ?? 0,
          data: mergeUniqueBy([...prev, ...next], 'support_id' as any),
        };
      }

      state.lastUsersQueryKey = key ?? null;
      state.pendingUsersQueryKey = null;
    },

    subscribeAllRequest: (state) => {},
    subscribeAllSuccess: (state, action: PayloadAction<ISupportsInfo>) => {
      state.reportsLoader = false;
      state.subscribeAll = action.payload;
    },
    subscribeRequest: (state) => {},
    subscribeSuccess: (state, action: PayloadAction<ISupportsInfo>) => {
      state.reportsLoader = false;
      state.subscribeAll = action.payload;
    },

    getTypesRequest: (state) => {
      state.reportsLoader = true;
    },

    getTypesResponse: (state, action: PayloadAction<Array<ITypes>>) => {
      state.reportsLoader = false;
      state.types = action.payload;
    },

    getMassageRequest: (state, _action: PayloadAction<IMassages>) => {
      state.reportsLoader = true;
    },

    getMassageResponse: (state, action: PayloadAction<IMassagesResponse>) => {
      state.reportsLoader = false;
      state.massages = action.payload;

      const chats: Array<IContent> = action.payload.messages.map((chat) => {
        const obj = onJsonParseBigin(chat.body);
        return obj.admin_id ? obj : onJsonParseBigin(obj.input);
      });

      state.chatList = chats.map((chat) => {
        const media = chat?.outMessage?.fwd_from?.from_id
          ? getForwardMedia(chat?.outMessage?.media)
          : {};
        const base = {
          admin: false as const,
          message: chat?.outMessage?.message || chat?.input,
          messageId: chat?.messageId,
          hash:
            String(chat?.media?.document?.access_hash).replace(/[^+-\d]/g, '') ||
            String(chat?.media?.photo_FLAGPHOTO?.access_hash).replace(/[^+-\d]/g, ''),
          type:
            chat?.media?.document?.predicate_name || chat?.media?.photo_FLAGPHOTO?.predicate_name,
          mime_type: chat?.media?.document?.mime_type,
          file_name: chat?.media?.document?.attributes?.[0]?.file_name,
          create_at: chat?.outMessage?.date,
          // нові поля — тільки якщо є форвард
          fwd_message: chat?.outMessage?.fwd_from ? chat?.outMessage?.message : undefined,
          fwd_name: chat?.outMessage?.fwd_from?.from_id
            ? chat?.outMessage?.fwd_from?.from_name?.value
            : undefined,
          fwd_date: chat?.outMessage?.fwd_from?.date
            ? String(chat?.outMessage?.fwd_from?.date) // <-- ключовий рядок
            : undefined,
          reply_to_msg_id: chat.outMessage?.reply_to?.reply_to_msg_id
            ? String(chat.outMessage?.reply_to?.reply_to_msg_id)
            : undefined,
          ...media,
        };

        if (chat.input || chat.document) {
          return {
            admin: true as const,
            message: chat.input,
            hash: String(chat?.document?.access_hash).replace(/[^+-\d]/g, '') || '',
            type: chat?.document?.predicate_name,
            mime_type: chat?.document?.mime_type,
            file_name: chat?.document?.attributes?.[0]?.file_name,
            create_at: chat?.document?.date ?? chat?.time,
            // без форварду: ці лишаються undefined
            fwd_message: undefined,
            fwd_name: undefined,
            fwd_date: undefined,
            messageId: chat?.messageId,
          };
        }

        return base;
      });
    },

    removeListChats: (state) => {
      state.tickets = null;
    },

    changeStatusRequest: (state, _action: PayloadAction<IChangeStatusData>) => {
      state.reportsLoader = true;
    },

    changeStatusResponse: (state, action: PayloadAction<IChangeStatusData>) => {
      const { payload } = action;

      if (
        state.tickets?.data &&
        2 === payload.data.type &&
        (payload.btnTabName.toLowerCase() === 'current' || payload.btnTabName === '')
      ) {
        state.tickets.data = state.tickets.data.filter(
          (ticket) => ticket.support_id !== payload.data.id,
        );
      }

      if (
        state.tickets?.data &&
        (0 === payload.data.type || 1 === payload.data.type) &&
        payload.btnTabName.toLowerCase() === 'previos'
      ) {
        state.tickets.data = state.tickets.data.filter(
          (ticket) => ticket.support_id !== payload.data.id,
        );
      }

      if (state.users) {
        state.users = {
          data: state.users.data.map((user) => {
            if (payload.data.id === user.support_id) {
              return {
                ...user,
                status: {
                  ...user.status,
                  Int64: payload.data.type as number,
                },
              };
            }
            return user;
          }),
          total: state.users.total,
        };
      }
      state.reportsLoader = false;
    },

    changeTypeRequest: (state, _action: PayloadAction<IChangeStatus>) => {
      state.reportsLoader = true;
    },

    changeTypeResponse: (state, action: PayloadAction<IChangeStatus>) => {
      const { id, type } = action.payload;
      state.reportsLoader = false;

      if (state.tickets?.data) {
        state.tickets.data.forEach((ticket) => {
          if (ticket.support_id === id) {
            state.users &&
              state.users.data.forEach((user) => {
                if (user.user_id === ticket.user_id) {
                  user.type_id.Int64 = type as number;
                  user.type_name.String = state.types?.find((el) => el.Id === type)
                    ?.Title as string;
                }
              });
            ticket.type_id.Int64 = type as number;
            ticket.type_name.String = state.types?.find((el) => el.Id === type)?.Title as string;
          }
        });
      }
    },

    getNotesRequest: (state, _action: PayloadAction<string>) => {
      state.reportsLoader = true;
    },
    getNotesResponse: (state, action: PayloadAction<Array<INote>>) => {
      state.reportsLoader = false;
      state.notes = action.payload;
    },

    creareNoteRequest: (state, _action: PayloadAction<INoteRequest>) => {
      state.reportsLoader = true;
    },
    createNoteResponse: (state, action: PayloadAction<INote>) => {
      state.reportsLoader = false;
      if (state.notes) {
        state.notes?.push(action.payload);
      } else {
        state.notes = [action.payload];
      }
    },

    editNoteRequest: (state, _action: PayloadAction<INoteRequest>) => {
      state.reportsLoader = true;
    },
    editNoteResponse: (state, action: PayloadAction<INote>) => {
      state.reportsLoader = false;
      state.notes?.forEach((note) => {
        if (note.id === action.payload.id) note.text.String = action.payload.text.String;
      });
    },

    deleteNoteRequest: (state, _action: PayloadAction<string>) => {
      state.reportsLoader = true;
    },
    deleteNoteResponse: (state, action: PayloadAction<number>) => {
      state.reportsLoader = false;
      if (state.notes) state.notes = state.notes?.filter((note) => note.id !== action.payload);
    },

    onConect: (state, action: PayloadAction<string>) => {
      state.client = action.payload;
    },

    ticketsRequest: (state, _action: PayloadAction<ITicketsRequest>) => {
      state.reportsLoader = true;
    },
    ticketsResponse: (state, action: PayloadAction<ITicketsResponse | null>) => {
      state.reportsLoader = false;
      state.tickets = action.payload;
    },

    uploadFileRequest: (state, _action: PayloadAction<IUploadFileRequred>) => {
      state.uploadLoader = true;
    },
    uploadFileResponse: (state, action: PayloadAction<any>) => {
      state.uploadLoader = false;
      state.tickets = action.payload;
    },

    setMassage: (
      state,
      action: PayloadAction<{
        admin: boolean;
        message: string;
        hash?: string;
        type?: string;
        mime_type?: string;
        file_name?: string;
        create_at?: number;
        fwd_message?: string;
        fwd_name?: string;
        fwd_date?: string;
        reply_to_msg_id?: string;
        messageId?: number;
      }>,
    ) => {
      const item = { ...action.payload, ts: Date.now() };
      state.chatList = state.chatList.concat(item);
    },

    setTicket: (
      state,
      action: PayloadAction<{
        hash?: string;
        type?: string;
        mime_type?: string;
        file_name?: string;
        message?: string;
        fwd_message?: string;
        fwd_name?: string;
        fwd_date?: string;
        reply_to_msg_id?: string;
        messageId?: number;
      }>,
    ) => {
      state.chatList?.push({
        admin: true,
        message: action.payload.message || '',
        mime_type: action.payload.mime_type,
        file_name: action.payload.file_name,
        create_at: toUnixSeconds(),
        hash: action.payload.hash,
      });
    },

    changeStatus: (state, action: PayloadAction<{ type: number; id: number }>) => {
      const { id, type } = action.payload;
      state.tickets?.data.forEach((ticket) => {
        if (ticket.support_id === id) ticket.status.Int64 = type;
      });
    },

    resetMassage: (state) => {
      state.chatList = [];
    },

    searchTickets: (state, action: PayloadAction<string>) => {
      state.search = action.payload;
    },

    setTickets: (state, action: PayloadAction<boolean>) => {
      state.ticket = action.payload;
    },

    reportsInitState: (state) => {
      state.reportsLoader = false;
      state.uploadLoader = false;
    },
  },
});

export default support.reducer;
export const {
  sendLetterRequest,
  getLetterSuccess,
  getUsersRequest,
  getUsersSuccess,
  subscribeAllRequest,
  subscribeAllSuccess,
  subscribeRequest,
  subscribeSuccess,
  getTypesRequest,
  getTypesResponse,
  getMassageRequest,
  getMassageResponse,
  changeStatusRequest,
  changeStatusResponse,
  changeTypeRequest,
  changeTypeResponse,
  getNotesRequest,
  getNotesResponse,
  creareNoteRequest,
  createNoteResponse,
  editNoteRequest,
  editNoteResponse,
  deleteNoteRequest,
  deleteNoteResponse,
  onConect,
  ticketsRequest,
  ticketsResponse,
  changeStatus,
  resetMassage,
  setMassage,
  setTickets,
  searchTickets,
  uploadFileRequest,
  uploadFileResponse,
  setTicket,
  reportsInitState,
  removeListChats,
} = support.actions;
